import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { LogOut, RefreshCw, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const waReply = (e) => {
  const digits = (e.phone || "").replace(/\D/g, "");
  const num = digits.length === 10 ? `91${digits}` : digits;
  const text = `Namaste ${e.name}! Thank you for your enquiry${e.tour ? ` about "${e.tour}"` : ""} on exploreindians.com. This is Explore India's concierge — I'd be delighted to plan your journey${e.travel_date ? ` around ${e.travel_date}` : ""}.`;
  return `https://wa.me/${num}?text=${encodeURIComponent(text)}`;
};

const Admin = () => {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [user, setUser] = useState(location.state?.user || null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const denied = new URLSearchParams(location.search).get("denied");

  useEffect(() => {
    if (location.state?.user) return;
    const check = async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const u = await res.json();
        setUser(u);
        setAuth(true);
      } catch {
        setAuth(false);
      }
    };
    check();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/enquiries`, { credentials: "include" });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      setData({ enquiries: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (auth === true) loadEnquiries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setAuth(false);
    setUser(null);
  };

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (auth === null) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center" data-testid="admin-loading">
        <p className="font-display text-2xl text-gold-light animate-pulse">Opening the royal ledger…</p>
      </div>
    );
  }

  if (auth === false) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center px-4" data-testid="admin-login">
        <div className="glass-card rounded-2xl border border-gold/25 p-10 max-w-md w-full text-center">
          <img src="/assets/logo.jpg" alt="Explore India" className="h-16 w-16 rounded-full object-cover ring-1 ring-gold/50 mx-auto" />
          <h1 className="mt-6 font-display text-3xl text-slate-50">Owner Dashboard</h1>
          <p className="mt-2 text-sm text-slate-400">Private area — sign in with the authorized Google account to view enquiries.</p>
          {denied && (
            <p className="mt-4 rounded-lg bg-red-500/10 border border-red-500/40 px-4 py-2.5 text-sm text-red-300" data-testid="admin-denied-msg">
              This Google account is not authorized.
            </p>
          )}
          <button
            onClick={login}
            data-testid="admin-google-login-btn"
            className="mt-6 w-full inline-flex items-center justify-center gap-3 rounded-full bg-gradient-to-r from-gold to-gold-amber px-6 py-3.5 text-sm font-bold text-obsidian hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-shadow"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true"><path fill="currentColor" d="M21.35 11.1h-9.17v2.73h6.51c-.33 3.81-3.5 5.44-6.5 5.44C8.36 19.27 5 16.25 5 12c0-4.1 3.2-7.27 7.2-7.27 3.09 0 4.9 1.97 4.9 1.97L19 4.72S16.56 2 12.1 2C6.42 2 2.03 6.8 2.03 12c0 5.05 4.13 10 10.22 10 5.35 0 9.25-3.67 9.25-9.09 0-1.15-.15-1.81-.15-1.81z"/></svg>
            Continue with Google
          </button>
          <a href="/" className="mt-5 inline-block text-xs text-slate-500 hover:text-gold-light transition-colors">← Back to exploreindians.com</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian text-slate-100" data-testid="admin-dashboard">
      <header className="border-b gold-hairline bg-ink/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/assets/logo.jpg" alt="Explore India" className="h-11 w-11 rounded-full object-cover ring-1 ring-gold/50" />
            <span className="leading-tight">
              <span className="block font-display text-lg font-semibold">Enquiries Ledger</span>
              <span className="block text-[10px] uppercase tracking-[0.25em] text-gold/80 font-mono">{user?.email}</span>
            </span>
          </a>
          <div className="flex items-center gap-3">
            <button
              onClick={loadEnquiries}
              data-testid="admin-refresh-btn"
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-gold-light hover:bg-gold/10 transition-colors"
            >
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-4 py-2 text-xs font-semibold text-slate-300 hover:bg-gold/10 transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="glass-card rounded-2xl border border-gold/15 p-6">
            <p className="font-display text-4xl text-gold-gradient font-semibold" data-testid="admin-total-count">{data?.total ?? "—"}</p>
            <p className="mt-1 text-xs uppercase tracking-widest text-slate-500">Total Enquiries</p>
          </div>
          <div className="glass-card rounded-2xl border border-gold/15 p-6">
            <p className="font-display text-4xl text-gold-gradient font-semibold">
              {data ? data.enquiries.filter((e) => e.whatsapp_status?.startsWith("queued")).length : "—"}
            </p>
            <p className="mt-1 text-xs uppercase tracking-widest text-slate-500">WhatsApp Sent</p>
          </div>
        </div>

        <div className="mt-10 space-y-4" data-testid="admin-enquiry-list">
          {data && data.enquiries.length === 0 && (
            <div className="glass-card rounded-2xl border border-gold/15 p-12 text-center">
              <Inbox size={32} className="mx-auto text-gold/50" />
              <p className="mt-4 text-slate-400">No enquiries yet. New form submissions appear here instantly.</p>
            </div>
          )}
          {data?.enquiries.map((e) => (
            <article key={e.id} data-testid={`admin-enquiry-${e.id}`} className="glass-card rounded-2xl border border-gold/15 p-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-xl text-slate-100">{e.name}</h3>
                  <p className="mt-1 text-sm text-slate-400 font-mono">
                    {e.email || "—"} · {e.phone || "—"}
                  </p>
                </div>
                <div className="text-right">
                  <span className={`inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ring-1 ${
                    e.whatsapp_status?.startsWith("queued")
                      ? "text-emerald-300 ring-emerald-400/40 bg-emerald-400/10"
                      : "text-slate-400 ring-slate-500/40 bg-slate-500/10"
                  }`}>
                    WhatsApp: {e.whatsapp_status?.startsWith("queued") ? "Sent" : !e.whatsapp_status || e.whatsapp_status === "not_configured" ? "Pending setup" : "Failed"}
                  </span>
                  <span className={`ml-2 inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-widest ring-1 ${
                    e.email_status?.startsWith("sent")
                      ? "text-emerald-300 ring-emerald-400/40 bg-emerald-400/10"
                      : "text-slate-400 ring-slate-500/40 bg-slate-500/10"
                  }`} data-testid={`admin-email-status-${e.id}`}>
                    Email: {e.email_status?.startsWith("sent") ? "Sent" : !e.email_status || e.email_status === "not_configured" ? "Pending setup" : "Failed"}
                  </span>
                  <p className="mt-2 text-xs text-slate-500 font-mono">{new Date(e.ts).toLocaleString("en-IN")}</p>
                </div>
              </div>
              <div className="mt-4 grid sm:grid-cols-4 gap-3 text-sm">
                <p><span className="text-gold/80 text-xs uppercase tracking-widest font-mono block">Tour</span>{e.tour || "Custom"}</p>
                <p><span className="text-gold/80 text-xs uppercase tracking-widest font-mono block">Date</span>{e.travel_date || "Flexible"}</p>
                <p><span className="text-gold/80 text-xs uppercase tracking-widest font-mono block">Guests</span>{e.guests || "—"}</p>
                <p><span className="text-gold/80 text-xs uppercase tracking-widest font-mono block">Tier</span>{e.hotel_tier || "—"}</p>
              </div>
              {e.message && <p className="mt-4 text-sm text-slate-300 border-t border-gold/10 pt-4">“{e.message}”</p>}
              {e.phone ? (
                <a
                  href={waReply(e)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`admin-reply-${e.id}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-xs font-bold text-white hover:shadow-[0_0_20px_rgba(37,211,102,0.45)] transition-shadow"
                >
                  <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current" aria-hidden="true"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38c1.45.79 3.08 1.21 4.79 1.21 5.46 0 9.91-4.45 9.91-9.91C21.95 6.45 17.5 2 12.04 2zm4.52 11.87c-.25-.12-1.47-.72-1.69-.81-.23-.08-.39-.12-.56.13-.17.25-.64.8-.78.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.41-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.22.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.56.12.17 1.75 2.67 4.23 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.06-.11-.22-.17-.47-.29z"/></svg>
                  Reply on WhatsApp
                </a>
              ) : (
                <p className="mt-5 text-xs text-slate-600 italic">No phone number provided — reply by email.</p>
              )}
            </article>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Admin;
